import { documentHeaders, admitBrowserDocument } from '../utils/browserPresence';
import React, { useEffect, useState } from 'react';

export default function AccountSessionWarning({ api, userId }) {
  const [warning, setWarning] = useState(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('dismissedSessionWarning'));
  useEffect(() => {
    if (!userId) return;
    let stopped = false;
    let busy = false;
    let timer;
    let delay = 15000;
    let controller;
    let acknowledged = false;
    const poll = async () => {
      if (stopped || busy) return;
      clearTimeout(timer);
      busy = true;
      controller = new AbortController();
      const requestTimeout = setTimeout(() => controller.abort(), 10000);
      try {
        const token = sessionStorage.getItem('token');
        if (!token) return;
        await admitBrowserDocument(api, token);
        if (stopped) return;
        const headers = { Authorization: `Bearer ${token}`, ...documentHeaders() };
        // Retry acknowledgement after an offline dismissal, including after reload.
        if (dismissed && !acknowledged) {
          const ack = await fetch(`${api}/notifications/${encodeURIComponent(dismissed)}/read`, {
            method: 'PATCH', headers, signal: controller.signal, cache: 'no-store',
          });
          if (!ack.ok) throw new Error('Notification acknowledgement unavailable');
          acknowledged = true;
        }
        const response = await fetch(`${api}/notifications?security_only=true`, {
          headers, signal: controller.signal, cache: 'no-store',
        });
        if (response.status === 401) return; // Do not navigate or disturb work from a background poll.
        if (!response.ok) throw new Error('Notifications unavailable');
        const rows = await response.json();
        if (!stopped) setWarning(rows[0] || null);
        delay = document.hidden ? 60000 : 15000;
      } catch (_) {
        delay = Math.min(delay * 2, 120000);
      } finally {
        clearTimeout(requestTimeout);
        busy = false;
        if (!stopped) timer = setTimeout(poll, delay);
      }
    };
    const resume = () => { if (!document.hidden) poll(); };
    poll();
    window.addEventListener('online', resume);
    document.addEventListener('visibilitychange', resume);
    return () => {
      stopped = true;
      clearTimeout(timer);
      controller?.abort();
      window.removeEventListener('online', resume);
      document.removeEventListener('visibilitychange', resume);
    };
  }, [api, userId, dismissed]);

  if (!warning || warning.id === dismissed) return null;
  return (
    <aside role="status" data-testid="account-session-warning"
      className="sticky top-0 z-40 flex items-start gap-4 border-b border-amber-300 bg-amber-50 px-5 py-3 text-sm text-amber-950">
      <p className="flex-1">{warning.message}</p>
      <button type="button" className="shrink-0 rounded px-2 py-1 font-semibold hover:bg-amber-100"
        aria-label="Dismiss account sign-in warning" onClick={() => {
          sessionStorage.setItem('dismissedSessionWarning', warning.id);
          setDismissed(warning.id);
        }}>Dismiss</button>
    </aside>
  );
}
